// Pure helpers behind the Security Operations console visuals (/soc): posture, KPI series, queue
// insights. Everything is computed from the summary tiles, the trends response and the queue rows the
// page already loads; nothing here invents a figure.

import { freshnessOf, isConnected } from "./socFreshness";
import { SOC_TILES } from "./socTileConfig";
import { isUnassigned } from "./socQueue";

const n = (v) => Number(v) || 0;
const has = (tile, key) => Object.prototype.hasOwnProperty.call(tile?.metrics || {}, key);

// ── Posture ──────────────────────────────────────────────────────────────

// True when a connected area sent none of its figures yet (every one pending): there is nothing to
// call healthy, so it reads "No figures yet" and counts as one to review.
export const hasNoFigures = (tile) =>
  Object.keys(tile?.metrics || {}).length === 0 && (tile?.pending || []).length > 0;

// One area's posture. Stale wins over the tile's own status: its counts may be out of date.
export const postureOf = (tile, now) => {
  if (!isConnected(tile)) return "notConnected";
  if (freshnessOf(tile, now).state === "stale") return "stale";
  if (hasNoFigures(tile)) return "review";
  if (tile.status === "critical") return "action";
  if (tile.status === "warning") return "review";
  return "healthy";
};

export const postureCounts = (tiles, now) =>
  (tiles || []).reduce(
    (acc, t) => ({ ...acc, [postureOf(t, now)]: acc[postureOf(t, now)] + 1 }),
    { action: 0, review: 0, stale: 0, healthy: 0, notConnected: 0 }
  );

// ── KPI series ───────────────────────────────────────────────────────────

// New incidents + alerts per day, from the trends response's dense new-findings points.
export const dailyIncidents = (trends) =>
  (trends?.newFindings || []).map((p) => n(p.incidents) + n(p.alerts));

// This period against the previous one of the same length (trends.previous). Null when there is no
// previous figure, or nothing in either period. `pct` only when the previous figure is above 0.
export const periodChange = (current, previous) => {
  if (current == null || previous == null) return null;
  const delta = current - previous;
  if (delta === 0 && previous === 0) return null;
  return { delta, pct: previous > 0 ? Math.round((delta / previous) * 100) : null, previousNone: previous === 0 };
};

// New incidents + alerts in the previous period; null when the response has no previous totals.
export const previousIncidents = (trends) => {
  const p = trends?.previous?.newFindings;
  return p ? n(p.incidents) + n(p.alerts) : null;
};

// Mean time from creation to assignment over open queue rows that record both. assignedAt is the
// latest assignment, so a reassigned row measures creation to its last assignment. Null when no row
// qualifies.
export const meanTimeToAssign = (rows) => {
  const spans = (rows || [])
    .filter((r) => r.created && r.assignedAt && r.assignedAt >= r.created)
    .map((r) => r.assignedAt.getTime() - r.created.getTime());
  if (spans.length === 0) return null;
  return { ms: spans.reduce((a, b) => a + b, 0) / spans.length, count: spans.length };
};

// "35 min", "4.2 h", "2.1 days".
export const formatDuration = (ms) => {
  const minutes = ms / 60000;
  if (minutes < 60) return { value: String(Math.max(1, Math.round(minutes))), unit: "min" };
  const hours = minutes / 60;
  if (hours < 48) return { value: hours.toFixed(1), unit: "h" };
  return { value: (hours / 24).toFixed(1), unit: "days" };
};

// Open critical + high rows, and how many of them have no owner.
export const urgentOwnership = (rows) => {
  const urgent = (rows || []).filter((r) => r.severity === "critical" || r.severity === "high");
  const unassigned = urgent.filter(isUnassigned).length;
  return { urgent: urgent.length, unassigned };
};

// ── Area tiles ───────────────────────────────────────────────────────────

// The daily series an area tile can draw as a mini chart; null for areas without one.
export const areaTrend = (key, trends) => {
  if (!trends) return null;
  const pick = (points, fn) => (points || []).map(fn);
  switch (key) {
    case "incidents":
      return dailyIncidents(trends);
    case "endpoint":
      return pick(trends.newFindings, (p) => n(p.vulnerabilities));
    case "external":
      return pick(trends.externalEvents, (p) => n(p.esm) + n(p.darkWeb) + n(p.secrets) + n(p.brand) + n(p.ransomware));
    case "network":
      return pick(trends.networkSignals, (p) => n(p.opened));
    case "identity":
      return pick(trends.identityExposure, (p) => n(p.critical) + n(p.high));
    default:
      return null;
  }
};

// ── Open issues ──────────────────────────────────────────────────────────

// An area's open issues: its config's issueKeys that came back, each with its label and value. The
// Overview chart, the tile headline and the menu badge all show their sum, so the three never
// disagree, and the tile spells the sum out.
export const areaIssueParts = (tile) => {
  const config = SOC_TILES[tile?.key];
  if (!config?.issueKeys) return [];
  const labels = Object.fromEntries(config.metrics.map(([key, label]) => [key, label]));
  return config.issueKeys
    .filter((k) => has(tile, k))
    .map((k) => ({ key: k, label: labels[k] || k, value: n(tile.metrics[k]) }));
};

// Null when none of the area's issue figures came back.
export const areaIssueCount = (tile) => {
  const parts = areaIssueParts(tile);
  return parts.length > 0 ? parts.reduce((sum, p) => sum + p.value, 0) : null;
};

// What a tile leads with when none of its issue figures came back (an inventory with no findings
// read yet, say): its most telling figure; null when it has none of these either.
const HEADLINES = {
  incidents: (t) => (has(t, "openIncidents") || has(t, "openAlerts")
    ? [n(t.metrics.openIncidents) + n(t.metrics.openAlerts), "open incidents & alerts"] : null),
  identity: (t) => (has(t, "mfaPercent") ? [`${t.metrics.mfaPercent}%`, "MFA adoption"]
    : has(t, "identities") ? [t.metrics.identities, "identities"] : null),
  endpoint: (t) => (has(t, "openCves") ? [t.metrics.openCves, "open CVEs"]
    : has(t, "devices") ? [t.metrics.devices, "devices"] : null),
  cloud: (t) => {
    const keys = ["defenderFailing", "awsFailing", "gcpFailing", "wizFailing"].filter((k) => has(t, k));
    if (keys.length) return [keys.reduce((s, k) => s + n(t.metrics[k]), 0), "failing cloud checks"];
    return has(t, "attackPaths") ? [t.metrics.attackPaths, "attack paths"] : null;
  },
  cloudApps: (t) => (has(t, "activeAlerts") ? [t.metrics.activeAlerts, "active alerts"]
    : has(t, "highRiskApps") ? [t.metrics.highRiskApps, "high-risk apps"] : null),
  siem: (t) => (has(t, "sentinelOpenIncidents") ? [t.metrics.sentinelOpenIncidents, "Sentinel open incidents"]
    : has(t, "splunkFailing") ? [t.metrics.splunkFailing, "failing Splunk health checks"] : null),
  detectionRules: (t) => (has(t, "sentinelActiveRules") ? [t.metrics.sentinelActiveRules, "Sentinel active rules"]
    : has(t, "splunkAlertRules") ? [t.metrics.splunkAlertRules, "Splunk alert rules"] : null),
  network: (t) => (has(t, "openSignals") ? [t.metrics.openSignals, "open firewall signals"] : null),
  external: (t) => {
    const keys = SOC_TILES.external.issueKeys.filter((k) => has(t, k));
    return keys.length ? [keys.reduce((s, k) => s + n(t.metrics[k]), 0), "open external risks"] : null;
  },
  data: (t) => (has(t, "aiDataLeaks") ? [t.metrics.aiDataLeaks, "AI app data-leak alerts"] : null),
  email: (t) => (has(t, "emailSettingsFailing") ? [t.metrics.emailSettingsFailing, "failing email settings"]
    : has(t, "domainsMissingEmailAuth") ? [t.metrics.domainsMissingEmailAuth, "domains missing email records"] : null),
  agenticAi: (t) => (has(t, "agents") ? [t.metrics.agents, "AI agents"] : null),
};

// The one figure an area tile leads with, and what it counts: the area's open issues (as the Overview
// chart and the menu badge count them), else its fallback figure.
export const areaHeadline = (tile) => {
  if (!isConnected(tile)) return null;
  const issues = areaIssueCount(tile);
  if (issues != null) return [issues, SOC_TILES[tile.key].issueLabel || "open issues"];
  return HEADLINES[tile.key] ? HEADLINES[tile.key](tile) : null;
};

// ── Posture gauges ───────────────────────────────────────────────────────

// Coverage against Axaris targets, one gauge per figure that exists. `pct` is the share of the target
// reached (capped at 100 for the arc); `label` is what the centre shows; `gap` says how far the figure
// is from its target (the gauge's hover readout).
export const postureGauges = (tiles) => {
  const byKey = Object.fromEntries((tiles || []).filter(isConnected).map((t) => [t.key, t]));
  const out = [];
  const identity = byKey.identity;
  if (has(identity, "mfaPercent")) {
    const v = n(identity.metrics.mfaPercent);
    out.push({ key: "mfa", title: "MFA adoption", detail: "target 90%", label: `${v}%`, pct: Math.min(100, (v / 90) * 100), met: v >= 90,
      gap: v >= 90 ? "Target of 90% met" : `${90 - v} points below the 90% target` });
  }
  const email = byKey.email;
  if (has(email, "emailSettings") && has(email, "emailSettingsFailing") && n(email.metrics.emailSettings) > 0) {
    const total = n(email.metrics.emailSettings);
    const passing = total - n(email.metrics.emailSettingsFailing);
    const v = Math.round((passing / total) * 100);
    out.push({ key: "email", title: "Email settings passing", detail: `${passing} of ${total}`, label: `${v}%`, pct: v, met: passing === total,
      gap: passing === total ? "Every setting passing" : `${total - passing} of ${total} settings failing` });
  }
  const rules = byKey.detectionRules;
  if (has(rules, "sentinelActiveRules")) {
    const v = n(rules.metrics.sentinelActiveRules);
    out.push({ key: "sentinel", title: "Sentinel rules", detail: "Axaris baseline 10", label: String(v), pct: Math.min(100, (v / 10) * 100), met: v >= 10,
      gap: v >= 10 ? `${v - 10} above the baseline of 10` : `${10 - v} below the baseline of 10` });
  }
  if (has(rules, "splunkAlertRules")) {
    const v = n(rules.metrics.splunkAlertRules);
    out.push({ key: "splunk", title: "Splunk alert rules", detail: "Axaris baseline 5", label: String(v), pct: Math.min(100, (v / 5) * 100), met: v >= 5,
      gap: v >= 5 ? `${v - 5} above the baseline of 5` : `${5 - v} below the baseline of 5` });
  }
  return out;
};

// ── Queue insights ───────────────────────────────────────────────────────

export const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

// Open rows by the viewer's local weekday (Monday first) and hour of creation.
export const arrivalMatrix = (rows) => {
  const m = WEEKDAYS.map(() => Array(24).fill(0));
  (rows || []).forEach((r) => {
    if (!r.created) return;
    const day = (r.created.getDay() + 6) % 7;
    m[day][r.created.getHours()] += 1;
  });
  return m;
};

// 0-4 shade for a count against the busiest cell.
export const heatLevel = (count, max) => (count <= 0 || max <= 0 ? 0 : Math.min(4, Math.ceil((count / max) * 4)));

export const AGE_BUCKETS = [
  ["Under 1 day", 0, 1],
  ["1-3 days", 1, 3],
  ["3-7 days", 3, 7],
  ["7-30 days", 7, 30],
  ["Over 30 days", 30, Infinity],
];
const DAY_MS = 24 * 60 * 60 * 1000;

// Open rows per age bucket, split by owner. Rows without a creation time are left out.
export const ageBuckets = (rows) =>
  AGE_BUCKETS.map(([label, from, to]) => {
    const inBucket = (rows || []).filter((r) => r.ageMs != null && r.ageMs >= from * DAY_MS && r.ageMs < to * DAY_MS);
    const unassigned = inBucket.filter(isUnassigned).length;
    return { label, unassigned, assigned: inBucket.length - unassigned };
  });

// Newest open rows first.
export const newestRows = (rows, count = 8) =>
  [...(rows || [])].filter((r) => r.created).sort((a, b) => b.created - a.created).slice(0, count);

// The newest open critical row, else the newest high one.
export const latestUrgent = (rows) => {
  const newest = (sev) => newestRows((rows || []).filter((r) => r.severity === sev), 1)[0] || null;
  return newest("critical") || newest("high");
};
